import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useAuth from "@/hooks/useAuth";
import reviewsApi from "@/api/reviewsApi";
import ReviewPage from "@/model/ReviewPage";
import ReviewResponse from "@/model/ReviewResponse";
import isoDate from "@/utils/isoDate";
import "@/components/PropertyReviewsPanel/PropertyReviewsPanel.scss";

const PAGE_SIZE = 10;

interface PropertyReviewsPanelProps {
  propertyId: string;
}

/** Guest reviews of a property. Read-only for hosts; admins can delete (moderation). */
function PropertyReviewsPanel({ propertyId }: PropertyReviewsPanelProps) {
  const { t, i18n } = useTranslation();
  const { session } = useAuth();

  const [summary, setSummary] = useState<ReviewPage | null>(null);
  const [reviews, setReviews] = useState<ReviewResponse[]>([]);
  const [page, setPage] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState(false);

  useEffect(() => {
    if (!session) {
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    setError(false);

    reviewsApi
      .listForProperty(session.accessToken, propertyId, page, PAGE_SIZE)
      .then((result) => {
        if (!cancelled) {
          setSummary(result);
          setReviews(result.content ?? []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [session, propertyId, page]);

  if (!session) {
    return null;
  }

  const isAdmin = session.role === "ADMIN";
  const totalPages = summary?.totalPages ?? 0;

  const handleDelete = async (review: ReviewResponse) => {
    if (!review.id || !window.confirm(t("reviews.deleteConfirm"))) {
      return;
    }
    setDeletingId(review.id);
    setDeleteError(false);
    try {
      await reviewsApi.deleteReview(session.accessToken, review.id);
      setReviews((current) => current.filter((item) => item.id !== review.id));
    } catch {
      setDeleteError(true);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="property-reviews-panel">
      <div className="property-reviews-panel__header">
        <h2>{t("reviews.title")}</h2>
        {summary &&
          (summary.reviewCount ?? 0) > 0 &&
          summary.averageRating !== undefined && (
            <span className="property-reviews-panel__average">
              ★ {summary.averageRating.toFixed(1)} ·{" "}
              {t("reviews.count", { count: summary.reviewCount })}
            </span>
          )}
      </div>

      {deleteError && (
        <p className="property-reviews-panel__error">
          {t("reviews.deleteError")}
        </p>
      )}
      {isLoading && <p>{t("reviews.loading")}</p>}
      {!isLoading && error && <p role="alert">{t("reviews.error")}</p>}
      {!isLoading && !error && reviews.length === 0 && (
        <p>{t("reviews.empty")}</p>
      )}

      {!isLoading && !error && reviews.length > 0 && (
        <>
          <ul className="property-reviews-panel__list">
            {reviews.map((review) => (
              <li key={review.id}>
                <div className="property-reviews-panel__meta">
                  <strong>{review.authorName}</strong>
                  <span
                    className="property-reviews-panel__stars"
                    aria-label={t("reviews.stars", {
                      count: review.rating ?? 0,
                    })}
                  >
                    {"★".repeat(review.rating ?? 0)}
                    <span aria-hidden="true">
                      {"★".repeat(5 - (review.rating ?? 0))}
                    </span>
                  </span>
                  <span className="property-reviews-panel__date">
                    {review.createdAt
                      ? isoDate.formatIsoDate(
                          review.createdAt.slice(0, 10),
                          i18n.language
                        )
                      : "—"}
                  </span>
                  {isAdmin && review.id && (
                    <button
                      type="button"
                      onClick={() => handleDelete(review)}
                      disabled={deletingId === review.id}
                    >
                      {deletingId === review.id
                        ? t("reviews.deleting")
                        : t("reviews.deleteButton")}
                    </button>
                  )}
                </div>
                <p className="property-reviews-panel__comment">
                  {review.comment}
                </p>
              </li>
            ))}
          </ul>

          {totalPages > 1 && (
            <div className="property-reviews-panel__pagination">
              <button
                type="button"
                onClick={() => setPage((current) => current - 1)}
                disabled={page <= 0}
              >
                {t("pagination.previous")}
              </button>
              <span>
                {t("pagination.pageInfo", { page: page + 1, totalPages })}
              </span>
              <button
                type="button"
                onClick={() => setPage((current) => current + 1)}
                disabled={page + 1 >= totalPages}
              >
                {t("pagination.next")}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default PropertyReviewsPanel;
export type { PropertyReviewsPanelProps };
